import { zodResolver } from '@hookform/resolvers/zod'
import { Controller, useForm } from 'react-hook-form'
import { Button, Field, FieldHelp, FieldLabel, Input } from '@axilon-apps/libs-ui-shadcn'
import {
  FlashMessages,
  useFlashMessages,
  useFormValidationErrors
} from '@axilon-apps/libs-ui-components'
import type { FieldErrors } from '@axilon-apps/domains-identity/types'

import type { AddUserFormData } from '../../../../../../../../types/add-user-form'
import addUserValidationSchema from '../../../../../../../../validations/add-user-validation-schema'
import { UsersFlashMessageGroup } from '../../../../../../../../constants/users-flash-message-groups'

interface Props {
  initialValues: AddUserFormData
  onSubmit: (data: AddUserFormData) => void
  onCancel: () => void
  isLoading?: boolean
  fieldErrors?: FieldErrors<AddUserFormData>
  showEmail: boolean
}

const RenameUserForm = ({
  initialValues,
  onSubmit,
  onCancel,
  isLoading = false,
  fieldErrors,
  showEmail
}: Props) => {
  const {
    handleSubmit,
    control,
    formState: { errors }
  } = useForm({
    defaultValues: initialValues,
    resolver: zodResolver(addUserValidationSchema)
  })

  const flash = useFlashMessages(UsersFlashMessageGroup.RenameUser)
  const onValidationError = useFormValidationErrors(flash)

  return (
    <form onSubmit={handleSubmit(onSubmit, onValidationError)} noValidate>
      <div className="flex flex-col gap-6">
        <Controller
          control={control}
          name="name"
          render={({ field }) => (
            <Field data-invalid={Boolean(errors.name || fieldErrors?.name) || undefined}>
              <FieldLabel required>Name</FieldLabel>
              <Input {...field} type="text" />
              <FieldHelp>{errors.name?.message || fieldErrors?.name}</FieldHelp>
            </Field>
          )}
        />
        <Controller
          control={control}
          name="username"
          render={({ field }) => (
            <Field data-invalid={Boolean(errors.username || fieldErrors?.username) || undefined}>
              <FieldLabel required>Username</FieldLabel>
              <Input {...field} type="text" />
              <FieldHelp>{errors.username?.message || fieldErrors?.username}</FieldHelp>
            </Field>
          )}
        />
        {showEmail && (
          <Controller
            control={control}
            name="email"
            render={({ field }) => (
              <Field data-invalid={Boolean(errors.email || fieldErrors?.email) || undefined}>
                <FieldLabel>Email</FieldLabel>
                <Input {...field} type="email" />
                <FieldHelp>{errors.email?.message || fieldErrors?.email}</FieldHelp>
              </Field>
            )}
          />
        )}
        <FlashMessages group={UsersFlashMessageGroup.RenameUser} />
        <div className="flex flex-row justify-end gap-4">
          <Button type="button" variant="secondary" onClick={onCancel} disabled={isLoading}>
            Cancel
          </Button>
          <Button type="submit" disabled={isLoading}>
            Save
          </Button>
        </div>
      </div>
    </form>
  )
}

export default RenameUserForm
